import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { useGetSupportedEsignTokensQuery } from '@/store/query/esign-documents';
import type { EsignSupportedToken } from '@/types/esignature';

export interface EsignTokenGroup {
  name: string;
  tokens: EsignSupportedToken[];
}

/**
 * The tokens a stage e-sign document may contain, from the server.
 *
 * This replaced a six-entry constant in the frontend. The supported set is ~72
 * — six DocuSign placement anchors plus the merge tokens — so a hardcoded list
 * could only ever advertise a fraction of them, and every token it did not know
 * rendered with a title-cased fallback instead of its designed label.
 *
 * Keyed per account even though the set is global today: job and candidate
 * custom-field tokens are next on this feature and those are account-scoped.
 *
 * **Validity is not decided here.** The server validates uploaded documents and
 * returns `valid_tokens` / `invalid_tokens`; this is discovery and labelling
 * only. That separation is why a failed fetch degrades rather than blocks.
 */
export const useEsignSupportedTokens = () => {
  const currUser = useSelector(selectCurrUser);
  const accountId = currUser?.account?.id;

  const { data, isFetching, isError } = useGetSupportedEsignTokensQuery(
    { accountId },
    {
      // The catalogue does not change mid-session, and the rail mounts on every
      // wizard open. One request per account is enough.
      refetchOnMountOrArgChange: false,
    }
  );

  const tokens = useMemo<EsignSupportedToken[]>(() => data ?? [], [data]);

  /** Grouped for the rail, in the order the server lists them. */
  const groups = useMemo<EsignTokenGroup[]>(() => {
    const byName = new Map<string, EsignSupportedToken[]>();
    tokens.forEach((entry) => {
      const name = entry.group || 'Other';
      const existing = byName.get(name);
      if (existing) existing.push(entry);
      else byName.set(name, [entry]);
    });
    return [...byName.entries()].map(([name, entries]) => ({
      name,
      tokens: entries,
    }));
  }, [tokens]);

  const labels = useMemo(
    () => new Map(tokens.map(({ token, label }) => [token, label])),
    [tokens]
  );

  /**
   * Tokens the set must carry between them — today just the candidate
   * signature anchor.
   *
   * Set-level, not per document: one document in the set must carry it, which
   * is what `err_esign_candidate_signature_missing` enforces on save.
   */
  const requiredTokens = useMemo(
    () => tokens.filter(({ required }) => required).map(({ token }) => token),
    [tokens]
  );

  return {
    tokens,
    groups,
    labels,
    requiredTokens,
    isLoading: isFetching,
    /**
     * True when the catalogue could not be loaded. Callers that gate on
     * `requiredTokens` must not block the recruiter in that case — an empty
     * required list would otherwise read as "nothing satisfies it", and the
     * server enforces the same rule on save regardless.
     */
    isUnavailable: isError || (!isFetching && tokens.length === 0),
  };
};
